import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Output, Type } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { HotToastRef, Toast } from '@ngneat/hot-toast';
import { getTrackByFn } from '@y42/angular/util';
import { Icon, IconModule } from '@y42/core/icon';
import { loadingIcon } from '@y42/icons';
import { Subject } from 'rxjs';

export type ToastMessage = string | { title: string; description: string } | Type<unknown> | undefined;

export interface ToastAction {
  id: string;
  label: string;
}

export interface ToastData<Action extends ToastAction = ToastAction> {
  message?: ToastMessage;
  actionClicked: Subject<Action['id']>;
  actions?: Action[];
  [key: string]: unknown;
}

export interface ToastClose<Action extends ToastAction = ToastAction> {
  id: string;
  dismissedByAction: { actionId?: Action['id'] };
}

const ToastTypeToIcon: Record<Toast<unknown>['type'], Icon> = {
  success: 'check',
  error: 'circle-exclamation',
  warning: 'triangle-exclamation',
  info: 'circle-info',
  loading: loadingIcon,
  blank: 'circle-info',
};

@Component({
  selector: 'y42-toast',
  standalone: true,
  imports: [CommonModule, IconModule, MatButtonModule],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent<Action extends ToastAction = ToastAction> {
  constructor(public readonly toastRef: HotToastRef) {}

  @Output() actionClicked = new EventEmitter<Action['id']>();

  readonly toast = this.toastRef.getToast();
  readonly type = this.toast.type;
  readonly data = this.toast.data!;
  private readonly message = this.data['message'];
  readonly toastTitle = typeof this.message === 'object' ? this.message.title : this.message;
  readonly toastDescription = typeof this.message === 'object' ? this.message.description : undefined;
  readonly actions = this.data['actions'];
  readonly icon = ToastTypeToIcon[this.type];
  readonly trackByID = getTrackByFn<ToastAction>('id');

  close(id?: Action['id']) {
    this.actionClicked.emit(id);
    this.toastRef.close({
      dismissedByAction: true,
    });
  }
}
